import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as ReportsIcon,
  Timeline as TimelineIcon,
  Speed as PerformanceIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  ShoppingCart as OrderIcon
} from '@mui/icons-material';
// Mock data removed - will fetch from API

const RepTrends = ({ currentUser }) => {
  const [timeRange, setTimeRange] = useState('6_months');
  
  // Temporary placeholders - will be fetched from API
  const mockClinics = [];
  const mockCompanies = [];

  // Generate trend data
  const trendData = useMemo(() => {
    const months = [];
    const now = new Date();
    const numMonths = timeRange === '3_months' ? 3 : timeRange === '6_months' ? 6 : 12;
    
    for (let i = numMonths - 1; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = month.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      // Simulate trend data
      const baseSales = 180000 + (Math.random() * 140000);
      const orders = Math.floor(Math.random() * 25) + 15;
      const avgOrderValue = baseSales / orders;
      
      months.push({
        month: monthName,
        sales: Math.floor(baseSales),
        orders,
        avgOrderValue: Math.floor(avgOrderValue),
        commissionEarned: baseSales >= (currentUser?.commissionThreshold || 200000),
        growth: ((Math.random() - 0.5) * 30).toFixed(1) // -15% to +15%
      });
    }
    
    return months;
  }, [timeRange, currentUser]);

  // Calculate clinic performance
  const clinicPerformance = useMemo(() => {
    if (!currentUser?.clinicIds) return [];
    
    return currentUser.clinicIds.map(clinicId => {
      const clinic = mockClinics.find(c => c.id === clinicId);
      const company = mockCompanies.find(comp => comp.id === clinic?.companyId);
      
      // Simulate performance data
      const orders = Math.floor(Math.random() * 12) + 3;
      const revenue = Math.floor((Math.random() * 80000) + 20000);
      const avgOrderValue = revenue / orders;
      const lastOrderDays = Math.floor(Math.random() * 30) + 1;
      
      return {
        clinic: clinic?.name || 'Unknown Clinic',
        company: company?.name || 'Unknown Company',
        location: clinic?.location || 'Unknown Location',
        orders,
        revenue,
        avgOrderValue,
        lastOrderDays,
        trend: ((Math.random() - 0.5) * 40).toFixed(1) // -20% to +20%
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [currentUser]);

  // Calculate overall metrics
  const totalSales = trendData.reduce((sum, month) => sum + month.sales, 0);
  const totalOrders = trendData.reduce((sum, month) => sum + month.orders, 0);
  const avgGrowth = trendData.reduce((sum, month) => sum + parseFloat(month.growth), 0) / trendData.length;
  const commissionsEarned = trendData.filter(month => month.commissionEarned).length;

  const MetricCard = ({ title, value, subtitle, icon, color = '#0d9488', trend }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color, mb: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {parseFloat(trend) >= 0 ? (
                  <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981', mr: 0.5 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444', mr: 0.5 }} />
                )}
                <Typography variant="body2" sx={{ 
                  color: parseFloat(trend) >= 0 ? '#10b981' : '#ef4444', 
                  fontWeight: 600 
                }}>
                  {Math.abs(parseFloat(trend))}% vs last period
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const ProgressCard = ({ title, current, target, color = '#0d9488', subtitle }) => {
    const percentage = Math.min((current / target) * 100, 100);
    
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 2 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color }}>
              {typeof current === 'number' ? current.toLocaleString() : current}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              / {typeof target === 'number' ? target.toLocaleString() : target}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={percentage} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              bgcolor: '#e5e7eb',
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
                borderRadius: 4
              }
            }} 
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {percentage.toFixed(1)}% Complete
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
          Sales Trends & Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analyze your performance trends and clinic insights
        </Typography>
      </Box>

      {/* Time Range Filter */}
      <Box sx={{ mb: 4 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="3_months">Last 3 Months</MenuItem>
            <MenuItem value="6_months">Last 6 Months</MenuItem>
            <MenuItem value="12_months">Last 12 Months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Sales"
            value={`$${totalSales.toLocaleString()}`}
            subtitle={`${totalOrders} orders`}
            icon={<ReportsIcon />}
            color="#0d9488"
            trend={avgGrowth.toFixed(1)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Monthly Growth"
            value={`${avgGrowth.toFixed(1)}%`}
            subtitle="Period over period"
            icon={<TrendingUpIcon />}
            color={avgGrowth >= 0 ? "#10b981" : "#ef4444"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Commission Months"
            value={`${commissionsEarned}/${trendData.length}`}
            subtitle="Qualifying periods"
            icon={<StarIcon />}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Clinics"
            value={clinicPerformance.length}
            subtitle="In your territory"
            icon={<LocationIcon />}
            color="#0f766e"
          />
        </Grid>
      </Grid>

      {/* Progress Tracking */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ProgressCard
            title="Current Month Goal"
            current={trendData[trendData.length - 1]?.sales || 0}
            target={currentUser?.commissionThreshold || 200000}
            color="#0d9488"
            subtitle="To commission"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProgressCard
            title="Annual Goal Progress"
            current={totalSales}
            target={2400000}
            color="#10b981"
            subtitle="YTD Progress"
          />
        </Grid>
      </Grid>

      {/* Monthly Trend Chart (Simplified Table Format) */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Monthly Performance Trend
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Month</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Sales</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Orders</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Avg Order Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Growth</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Commission</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trendData.map((month, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                    <TableCell sx={{ color: '#0f172a', fontWeight: 600 }}>
                      {month.month}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      ${month.sales.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {month.orders}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      ${month.avgOrderValue.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {parseFloat(month.growth) >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981', mr: 0.5 }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444', mr: 0.5 }} />
                        )}
                        <Typography variant="body2" sx={{ 
                          color: parseFloat(month.growth) >= 0 ? '#10b981' : '#ef4444',
                          fontWeight: 600
                        }}>
                          {month.growth}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={month.commissionEarned ? 'Earned' : 'Not Earned'}
                        size="small"
                        sx={{
                          bgcolor: month.commissionEarned ? '#dcfce7' : '#fee2e2',
                          color: month.commissionEarned ? '#16a34a' : '#dc2626',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Clinic Performance */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Clinic Performance Breakdown
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Clinic</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Orders</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Revenue</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Avg Order Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Trend</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Last Order</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clinicPerformance.map((clinic, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {clinic.clinic}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {clinic.company}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {clinic.location}
                    </TableCell>
                    <TableCell sx={{ color: '#0f172a', fontWeight: 600 }}>
                      {clinic.orders}
                    </TableCell>
                    <TableCell sx={{ color: '#0f172a', fontWeight: 600 }}>
                      ${clinic.revenue.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      ${clinic.avgOrderValue.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {parseFloat(clinic.trend) >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981', mr: 0.5 }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444', mr: 0.5 }} />
                        )}
                        <Typography variant="body2" sx={{ 
                          color: parseFloat(clinic.trend) >= 0 ? '#10b981' : '#ef4444',
                          fontWeight: 600
                        }}>
                          {Math.abs(parseFloat(clinic.trend))}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {clinic.lastOrderDays} days ago
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RepTrends;